/**
 * Image proxy utilities for steemitimages.com
 *
 * Transforms image URLs to use the steemitimages.com proxy for:
 * - Resizing (default: 640x480)
 * - Caching via CDN
 * - Reduced bandwidth
 */

const STEEMIT_PROXY_BASE = 'https://steemitimages.com';
const PROXY_PATTERN = /^https?:\/\/steemitimages\.com\/(\d+x\d+|0x0)\/(.+)$/;

/**
 * Check if a URL is already using the steemitimages.com proxy
 */
export function isProxiedUrl(url: string): boolean {
  if (!url) return false;
  return PROXY_PATTERN.test(url);
}

/**
 * Extract the original URL from a proxied steemitimages.com URL
 */
export function extractOriginalUrl(proxiedUrl: string): string {
  if (!proxiedUrl) return '';

  const match = proxiedUrl.match(PROXY_PATTERN);
  if (match && match[2]) {
    return match[2];
  }
  return proxiedUrl;
}

/**
 * Transform an image URL to use the steemitimages.com proxy
 *
 * @param url - Original image URL
 * @param width - Target width (default: 640)
 * @param height - Target height (default: 480)
 * @returns Proxied image URL
 *
 * @example
 * getProxiedImageUrl('https://example.com/image.jpg')
 * // => 'https://steemitimages.com/640x480/https://example.com/image.jpg'
 *
 * @example
 * getProxiedImageUrl('https://steemitimages.com/1024x768/https://example.com/image.jpg')
 * // => 'https://steemitimages.com/640x480/https://example.com/image.jpg'
 */
export function getProxiedImageUrl(
  url: string,
  width = 640,
  height = 480
): string {
  if (!url) return '';

  // Extract original URL if already proxied
  const originalUrl = extractOriginalUrl(url);

  return `${STEEMIT_PROXY_BASE}/${width}x${height}/${originalUrl}`;
}
